// frontend/src/pages/Dashboard.tsx

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TrendingUp, Zap, DollarSign, Activity, BookOpen } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { usePriceStore } from '@/store/usePriceStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import api from '@/lib/api';
import type { Trade } from '@/types';

export function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { prices, fetchPrices } = usePriceStore();
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPrices();
    loadRecentTrades();
  }, []);

  const loadRecentTrades = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/trades/user/history?limit=10');
      setRecentTrades(response.data.trades);
    } catch (error) {
      console.error('Failed to load trades:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const btcPrice = prices.get('btc');

  const stats = [
    {
      title: t('profile.total_trades'),
      value: user?.total_trades || 0,
      icon: <Activity className="w-6 h-6 text-apple-blue-500" />,
      color: 'bg-apple-blue-50',
    },
    {
      title: t('profile.total_volume_btc'),
      value: `${(user?.total_volume_btc || 0).toFixed(4)} BTC`,
      icon: <TrendingUp className="w-6 h-6 text-apple-green-500" />,
      color: 'bg-apple-green-50',
    },
    {
      title: 'BTC Price',
      value: btcPrice ? `$${btcPrice.usd_price.toLocaleString()}` : '...',
      icon: <DollarSign className="w-6 h-6 text-apple-orange-500" />,
      color: 'bg-apple-orange-50',
    },
    {
      title: t('profile.rating'),
      value: `${(user?.rating || 0).toFixed(1)} ⭐`,
      icon: <Zap className="w-6 h-6 text-apple-yellow-500" />,
      color: 'bg-apple-yellow-50',
    },
  ];

  return (
    <div className="min-h-screen bg-apple-gray-50 py-8">
      <div className="container-main">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            {t('nav.dashboard')}
          </h1>
          <p className="text-apple-gray-600">
            {t('dashboard.welcome', 'Welcome back')}, {user?.username}!
          </p>
        </div>

        {/* Guide Banner */}
        <div className="mb-6 bg-gradient-to-r from-apple-blue-50 to-blue-50 border border-apple-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-apple-blue-600" />
            <div>
              <p className="font-semibold text-apple-blue-800">{t('guide.dashboard_banner', 'New to SLE DEX?')}</p>
              <p className="text-sm text-apple-blue-600">{t('guide.dashboard_desc', 'Learn how to buy and sell BTC in 2 minutes')}</p>
            </div>
          </div>
          <Link to="/guide" className="px-4 py-2 bg-apple-blue-500 text-white rounded-lg text-sm font-medium hover:bg-apple-blue-600 transition-colors">{t('guide.nav', 'Guide')}</Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-apple-gray-600 mb-1">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-apple ${stat.color}`}>
                    {stat.icon}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Trades */}
        <Card>
          <CardHeader>
            <CardTitle>{t('trade.recent_trades')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-apple-gray-500">
                {t('common.loading')}
              </div>
            ) : recentTrades.length === 0 ? (
              <div className="text-center py-8 text-apple-gray-500">
                {t('dashboard.no_recent_trades', 'No recent trades')}
              </div>
            ) : (
              <div className="space-y-4">
                {recentTrades.map((trade) => (
                  <div
                    key={trade.id}
                    className="flex items-center justify-between p-4 bg-apple-gray-50 rounded-apple"
                  >
                    <div>
                      <div className="font-medium">
                        {trade.btc_amount} BTC ↔ {trade.asset_amount} {trade.asset?.symbol}
                      </div>
                      <div className="text-sm text-apple-gray-600">
                        {new Date(trade.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge
                      variant={
                        trade.status === 'completed'
                          ? 'success'
                          : trade.status === 'pending'
                          ? 'warning'
                          : 'gray'
                      }
                    >
                      {trade.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
